import { PxtraApiClientLegacy } from '@pxtra-client-lib-legacy';
import AuthenticationStore from '../authenticationStore';
import {
  action,
  computed,
  makeAutoObservable,
  makeObservable,
  observable,
  reaction,
  runInAction
} from 'mobx';
import { CancelablePromise } from '@pxtra-client-lib-legacy/gen';
import { Entity } from '../../lib/base/viewModels';
import { BaseStore } from './BaseStore';
import { EntityStream } from './EntityStream';
import { Event } from '../../hooks/useDrawer';
import { EntityList } from './EntityList';

export type BaseListRequestBody<TFilterModel extends object> = TFilterModel & {
  limit: number;
  offset: number;
  ordering?: string;
};

export type ListStoreFetchResult<TEntity extends Entity<TId>, TId = TEntity['id']> = {
  totalCount?: number;
  entities: TEntity[];
};

type BaseFetchResult<TFetchResultItem> = {
  count?: number;
  results?: TFetchResultItem[];
};

class EntityFilter<TEntity extends Entity<TId>, TId = TEntity['id']> {
  public filteredEntityCount = 0;
  public onResultChanged = new Event<(count: number) => void>();

  constructor(private filter?: (item: TEntity) => boolean) {
    makeObservable(this, {
      filteredEntityCount: observable
    });
    this.onResultChanged.subscribe((count) => {
      runInAction(() => {
        this.filteredEntityCount = count;
      });
    });
  }

  public apply(entities: TEntity[]) {
    return this.filter ? entities.filter(this.filter) : entities;
  }

  public define(predicate: (item: TEntity) => boolean) {
    this.filter = predicate;
  }
}

class EntityAdmixture<TEntity extends Entity<TId>, TId = TEntity['id']> {
  public prepend: EntityList<TEntity>;
  public append: EntityList<TEntity>;

  constructor() {
    this.prepend = new EntityList<TEntity>();
    this.append = new EntityList<TEntity>();
    makeAutoObservable(this);
  }

  public get length() {
    return this.prepend.length + this.append.length;
  }

  public clear() {
    this.prepend.clear();
    this.append.clear();
  }
}

export interface ListStoreFilterModel<
  TBackendFilterModel extends object,
  TEntity extends Entity<TId>,
  TId = TEntity['id']
> {
  backend: TBackendFilterModel;
  viewModel?: (entity: TEntity) => boolean;
}

export abstract class ListStore<
  TEntity extends Entity<TId>,
  TBackendFilterModel extends object,
  TFetchResult extends BaseFetchResult<TFetchResultItem>,
  TFetchResultItem extends object,
  TId = TEntity['id']
> extends BaseStore {
  static defaultPageSize = 25;

  public pageSize = ListStore.defaultPageSize;
  public page = 0;
  //TODO: data as computed based on entityStream and pageSize & page
  public data?: Array<TEntity>;
  public admixture: EntityAdmixture<TEntity>;
  public clientFilter = new EntityFilter<TEntity>();

  protected _filter?: ListStoreFilterModel<TBackendFilterModel, TEntity>;
  protected order?: string[];

  private entityStream: EntityStream<TEntity>;

  get totalCount() {
    return (
      (this.entityStream.length ?? 0) +
      this.admixture.length -
      (this.clientFilter?.filteredEntityCount ?? 0)
    );
  }

  constructor(apiClient: PxtraApiClientLegacy, authenticationStore: AuthenticationStore) {
    super(apiClient, authenticationStore);

    this.admixture = new EntityAdmixture();
    this.entityStream = new EntityStream<TEntity>(this.pageSize, (limit, offset) =>
      this.fetch(limit, offset)
    );

    makeObservable(this, {
      data: observable,
      page: observable,
      pageSize: observable,
      clientFilter: observable,
      filter: computed,
      totalCount: computed,
      setFilter: action,
      setPageSize: action,
      getPage: action,
      refresh: action
    });

    reaction(
      () => this.pageSize,
      (newSize) => {
        this.entityStream.changeBufferSize(newSize, Math.ceil(newSize / 2 / 5) * 5);
      }
    );

    reaction(
      () => this.entityStream.loading,
      (loading) => (this.loading = loading)
    );

    reaction(
      () => this.admixture.length,
      () => {
        runInAction(() => {
          this.getPage();
        });
      }
    );
  }
  private invalidate() {
    this.generation++;
    this.cancelPendingRequests();
    this.entityStream.clear();
    this.pendingPage = Promise.resolve();
    this.currentPagePointer = 0;
  }

  private isStale(generation: number) {
    return generation !== this.generation;
  }

  private cancelPendingRequests() {
    for (const request of this.pendingRequests) request.cancel();
    this.pendingRequests.clear();
  }

  async setPageSize(size: number) {
    this.pageSize = size;
    this.page = 0;
    this.entityStream.setPointer(0);
    this.currentPagePointer = 0;
    return this.getPage();
  }

  setFilter(filter: ListStoreFilterModel<TBackendFilterModel, TEntity>) {
    this.invalidate();
    this._filter = filter;
    if (filter?.viewModel) {
      this.admixture.prepend.setFilter(filter?.viewModel);
    } else {
      this.admixture.prepend.clearFilter();
    }
  }

  get filter() {
    return this._filter;
  }

  clearFilter() {
    this._filter = undefined;
  }

  setOrder(order: string[]) {
    this.invalidate();
    this.order = order;
  }

  clearOrder() {
    this.order = undefined;
  }

  public clear(): void {
    this.data = [];
    this.clearFilter();
    this.clearOrder();
    this.invalidate();
    this.admixture.clear();
  }

  public refresh() {
    this.invalidate();
    return this.getPage();
  }

  public async remove(entities: TEntity[]) {
    this.entityStream.remove(entities);
    await this.entityStream.read(1);
    this.entityStream.movePointer(-1);
  }

  protected abstract _fetch(
    baseBody: BaseListRequestBody<TBackendFilterModel>
  ): CancelablePromise<TFetchResult>;

  protected abstract mapItem(item: TFetchResultItem): TEntity;

  protected async fetch(limit: number, offset: number): Promise<ListStoreFetchResult<TEntity>> {
    const request = this._fetch(
      Object.assign(
        {},
        {
          limit,
          offset,
          ordering: this.order?.join(',')
        },
        this._filter?.backend
      )
    );
    /* Hold on to the CancelablePromise so invalidate() can abort the request
     * instead of waiting for a response it is going to throw away anyway. */
    this.pendingRequests.add(request);
    try {
      const result = await request;
      return { totalCount: result.count, entities: this.mapFetchResult(result) };
    } finally {
      this.pendingRequests.delete(request);
    }
  }

  private mapFetchResult(response?: TFetchResult) {
    return response?.results?.map((item) => this.mapItem(item)) ?? [];
  }

  private notOnPage(e: TEntity[]) {
    return e.filter((e1) => !this.data?.some((e2) => e1.id === e2.id));
  }

  private async readFromStream(direction: number, limit: number) {
    if (direction >= 0 || this.data?.length == 0) {
      return this.entityStream.read(limit);
    } else {
      return await this.entityStream.readReverse(limit);
    }
  }

  private currentPagePointer = 0;
  private pendingPage: Promise<void> = Promise.resolve();
  private generation = 0;
  private readonly pendingRequests = new Set<CancelablePromise<TFetchResult>>();

  /**
   * Composing a page interleaves awaited stream reads with mutations of the
   * shared stream pointer. Two overlapping calls therefore hand each other
   * overlapping slices and the page ends up holding the same entity twice.
   * Queueing the calls keeps every page composition atomic.
   */
  async getPage(page?: number) {
    const pageRequest = this.pendingPage.then(() => this.composePage(page));
    this.pendingPage = pageRequest.catch(() => undefined);
    return pageRequest;
  }

  private dedupeById(entities: Array<TEntity>) {
    const seen = new Set<TId | undefined>();
    return entities.filter((entity) => {
      // Entities without an id cannot be told apart, so leave them untouched.
      if (entity.id === undefined) return true;
      if (seen.has(entity.id)) return false;
      seen.add(entity.id);
      return true;
    });
  }

  private async composePage(page?: number) {
    const generation = this.generation;
    const direction = (page ?? this.page) - this.page;

    runInAction(() => {
      this.page = page ?? this.page;

      if (direction === 0) {
        this.entityStream.setPointer(this.currentPagePointer);
      }

      if (direction > 0) {
        this.currentPagePointer = this.entityStream.pointer;
      }
    });

    const prepend = this.admixture.prepend.readPage(this.page, this.pageSize);
    const newPage = new Array<TEntity>();
    let missingCount = this.pageSize - prepend.length;

    while (missingCount > 0) {
      let newEntities = await this.readFromStream(direction, missingCount);

      /* A company or filter change can land between two iterations. Everything
       * read so far belongs to the previous query, so drop the whole page. */
      if (this.isStale(generation)) return;

      if (newEntities.length == 0) break;
      if (direction !== 0) newEntities = this.notOnPage(newEntities);

      if (direction >= 0) {
        newPage.push(...this.clientFilter.apply(newEntities));
      } else {
        newPage.unshift(...this.clientFilter.apply(newEntities));
      }

      missingCount = this.pageSize - newPage.length - prepend.length;
    }

    newPage.unshift(...prepend);

    if (this.isStale(generation)) return;

    if (direction < 0) {
      this.currentPagePointer = this.entityStream.pointer;
    }

    runInAction(() => {
      this.data = this.dedupeById(newPage);
    });
  }

  public updateEntities(entityList: Array<TEntity>) {
    this.entityStream.updateEntities(entityList);
    this.getPage();
  }
}
